import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, html, list } from "../lib/bind";
import { setSelectOptions } from "../lib/formBind";
import { formatDate } from "../lib/date";
import { formatMoney } from "../lib/money";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { toast } from "../lib/toast";
import { buildBankingSubNav } from "./banking-subnav";
import {
  type BankAccount,
  type ImportPreview,
  type StatementImport,
  commitImport,
  discardImport,
  listBankAccounts,
  listStatementImports,
  statementImportDownloadUrl,
  uploadStatement,
} from "../services/bank";

const STATEMENT_IMPORT_STATUS_LABELS: Record<StatementImport["status"], string> = {
  pending_review: "Pending review",
  committed: "Committed",
  discarded: "Discarded",
};

export function bankingImportView(): Node {
  const accounts = signal<BankAccount[]>([]);
  const loadError = signal("");

  function reloadAccounts(): void {
    listBankAccounts()
      .then((result) => {
        accounts.value = result;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }
  reloadAccounts();

  const importAccountId = signal("");
  const preview = signal<ImportPreview | null>(null);
  const importing = signal(false);
  const fileInputRef = signal<HTMLInputElement | null>(null);

  const importHistory = signal<StatementImport[]>([]);

  function reloadImportHistory(): void {
    if (!importAccountId.value) {
      importHistory.value = [];
      return;
    }
    listStatementImports(importAccountId.value)
      .then((result) => {
        importHistory.value = result.items;
      })
      .catch(() => undefined);
  }

  async function doUpload(): Promise<void> {
    const input = fileInputRef.value;
    const file = input?.files?.[0];
    if (!importAccountId.value) {
      toast.error("Pick a bank account first.");
      return;
    }
    if (!file) {
      toast.error("Choose a CSV file.");
      return;
    }
    importing.value = true;
    try {
      preview.value = await uploadStatement(importAccountId.value, file);
      reloadImportHistory();
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Upload failed");
    } finally {
      importing.value = false;
      if (input) input.value = "";
    }
  }

  async function doCommit(): Promise<void> {
    const p = preview.value;
    if (!p) return;
    importing.value = true;
    try {
      const job = await commitImport(p.statement_import_id);
      if (job.status === "failed") {
        toast.error(job.error ?? "Import failed");
      } else {
        const imported = job.payload?.imported_count;
        const importedLabel = typeof imported === "number" ? String(imported) : "";
        toast.success(`Imported ${importedLabel} transaction(s).`);
        preview.value = null;
        reloadImportHistory();
      }
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Commit failed");
    } finally {
      importing.value = false;
    }
  }

  async function doDiscard(): Promise<void> {
    const p = preview.value;
    if (!p) return;
    try {
      await discardImport(p.statement_import_id);
      preview.value = null;
      reloadImportHistory();
      toast.success("Import discarded.");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Discard failed");
    }
  }

  const hasPreview = computed(() => preview.value !== null);

  const fragment = html`<div class="view">
    <p class="form-error"></p>

    <div class="card">
      <div class="card-label">Import a statement</div>
      <div class="form-row">
        <div class="form-field import-account-field">
          <label for="import-account">Bank account</label>
          <select id="import-account"><option value="">Select…</option></select>
        </div>
        <div class="form-field">
          <label for="import-file">CSV file</label>
          <input id="import-file" type="file" accept=".csv,text/csv" />
        </div>
        <div class="form-actions">
          <button class="btn btn-primary upload-btn" type="button">Upload &amp; preview</button>
        </div>
      </div>

      <div class="import-preview">
        <p class="preview-summary"></p>
        <p class="field-hint preview-gap-warning"></p>
        <div class="data-table import-preview-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Balance</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody class="preview-body"></tbody>
          </table>
        </div>
        <div class="form-actions">
          <button class="btn btn-primary commit-btn" type="button">Commit import</button>
          <button class="btn btn-danger discard-btn" type="button">Discard</button>
        </div>
      </div>

      <div class="import-history-section">
        <div class="card-label">Import history</div>
        <div class="data-table import-history-table">
          <table>
            <thead>
              <tr>
                <th>Filename</th>
                <th>Uploaded</th>
                <th>Period</th>
                <th>Rows</th>
                <th>Imported</th>
                <th>Duplicates</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody class="import-history-body"></tbody>
          </table>
        </div>
      </div>
    </div>
  </div>`;

  const viewEl = fragment.querySelector<HTMLElement>(".view");
  if (!viewEl) throw new Error("banking import view produced no .view element");
  viewEl.insertBefore(buildBankingSubNav("import"), viewEl.firstChild);

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const importAccountSelect = fragment.querySelector<HTMLSelectElement>("#import-account");
  const importFileInput = fragment.querySelector<HTMLInputElement>("#import-file");
  const uploadBtn = fragment.querySelector<HTMLButtonElement>(".upload-btn");
  const importPreviewEl = fragment.querySelector<HTMLElement>(".import-preview");
  const previewSummaryEl = fragment.querySelector<HTMLElement>(".preview-summary");
  const previewGapWarningEl = fragment.querySelector<HTMLElement>(".preview-gap-warning");
  const previewTable = fragment.querySelector<HTMLTableElement>(".import-preview-table table");
  const previewBody = fragment.querySelector<HTMLTableSectionElement>(".preview-body");
  const commitBtn = fragment.querySelector<HTMLButtonElement>(".commit-btn");
  const discardBtn = fragment.querySelector<HTMLButtonElement>(".discard-btn");
  const importHistoryTable = fragment.querySelector<HTMLTableElement>(".import-history-table table");
  const importHistoryBody = fragment.querySelector<HTMLTableSectionElement>(".import-history-body");

  if (
    !errorEl ||
    !importAccountSelect ||
    !importFileInput ||
    !uploadBtn ||
    !importPreviewEl ||
    !previewSummaryEl ||
    !previewGapWarningEl ||
    !previewTable ||
    !previewBody ||
    !commitBtn ||
    !discardBtn ||
    !importHistoryTable ||
    !importHistoryBody
  ) {
    throw new Error("banking import view markup missing expected elements");
  }

  bindErrorText(errorEl, loadError);

  fileInputRef.value = importFileInput;
  currentScope().effect(() => {
    setSelectOptions(
      importAccountSelect,
      accounts.value.map((a) => ({ value: a.id, label: a.name })),
      { placeholder: "Select…" },
    );
  });
  importAccountSelect.addEventListener("change", () => {
    importAccountId.value = importAccountSelect.value;
    reloadImportHistory();
  });
  uploadBtn.addEventListener("click", () => {
    void doUpload();
  });
  commitBtn.addEventListener("click", () => {
    void doCommit();
  });
  discardBtn.addEventListener("click", () => {
    void doDiscard();
  });
  bindShow(importPreviewEl, hasPreview);
  currentScope().effect(() => {
    const p = preview.value;
    if (!p) return;
    previewSummaryEl.textContent =
      `${String(p.row_count)} rows — ${String(p.valid_count)} to import, ` +
      `${String(p.error_count)} with errors, ${String(p.duplicate_count)} duplicates`;
    previewGapWarningEl.textContent = p.gap_warning ?? "";
    previewGapWarningEl.style.display = p.gap_warning ? "" : "none";
  });

  list(
    previewBody,
    computed(() => preview.value?.rows ?? []),
    (r) => String(r.row_number),
    (r) => {
      let status = r.errors.length ? r.errors.join("; ") : r.is_duplicate ? "Duplicate" : "OK";
      if (r.balance_warning) status += ` — ${r.balance_warning}`;
      const rowClass = r.errors.length
        ? "preview-row-error"
        : r.is_duplicate
          ? "preview-row-duplicate"
          : r.balance_warning
            ? "preview-row-warning"
            : "";
      const row = html`<tr class="${rowClass}">
        <td>${r.date ?? "—"}</td>
        <td>${r.description}</td>
        <td>${r.amount ? formatMoney(r.amount) : "—"}</td>
        <td>${r.balance ? formatMoney(r.balance) : "—"}</td>
        <td>${status}</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("preview row template produced no element");
      return labelRow(row, previewTable);
    },
  );

  list(
    importHistoryBody,
    importHistory,
    (h) => h.id,
    (h) => {
      const period =
        h.period_start && h.period_end
          ? `${formatDate(h.period_start)} – ${formatDate(h.period_end)}`
          : "—";
      const row = html`<tr>
        <td>${h.filename}</td>
        <td>${formatDate(h.created_at)}</td>
        <td>${period}</td>
        <td>${String(h.row_count)}</td>
        <td>${String(h.imported_count)}</td>
        <td>${String(h.duplicate_count)}</td>
        <td>${STATEMENT_IMPORT_STATUS_LABELS[h.status]}</td>
        <td><a class="btn import-history-download-link" target="_blank" rel="noopener">Download</a></td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("import history row template produced no element");
      const downloadLink = row.querySelector<HTMLAnchorElement>(".import-history-download-link");
      if (downloadLink) downloadLink.href = statementImportDownloadUrl(h.id);
      return labelRow(row, importHistoryTable);
    },
  );

  return fragment;
}
